import { CSSProperties } from 'react';
import styled from 'styled-components';
import {
  ComponentMatrixListValueHeaderCellContainer,
  HeaderCell,
  ListValueLabel,
} from './atoms';
import {
  ComponentMatrixDimension,
  ComponentMatrixGroupOwnerType,
} from '../../types';
import {
  ComponentMatrix2Commands,
  ComponentMatrix2HeaderCellModel,
} from '../types';
import { COMPONENT_HIERARCHY_PADDING, NONE_HEADER_LABEL } from '../../consts';
import { truncationPopoverAttrs } from './truncationPopover';
import {
  CARD_GROUP_BOTTOM_MARGIN,
  CARD_GROUP_TOP_MARGIN,
  DEFAULT_REPRESENTATION_DATA,
  ROW_HEADER_STRIPE_HEIGHT,
} from '../consts';
import { hasChildren, isLastChild } from '../../util';
import { RowHeaderComponent } from './rowHeaderComponent';
import { getRowHeaderCardStyle } from './getRowHeaderCellStyle';
import { getRowHeaderMargins } from './getRowHeaderMargins';
import { ArdoqId } from '@ardoq/api-types';

const GroupOutline = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none;
`;

type ComponentMatrixRowHeaderCellArgs = {
  headerCell: ComponentMatrix2HeaderCellModel;
  rowIndex: number;
  /** Controls the bottom of the GroupOutline overlay. Non-zero for cells at
   * any level whose last leaf row is a boundary row (rowBottomGap > 0). Stops
   * the border from bleeding into the inter-group gap encoded in the grid track. */
  cellBottomGap: number;
  /** Content height of the headerCell's first sub-row track. Sets the stripe
   * height for level-0 expanded groups so the colored bar covers the full
   * first sub-row body (instead of always being ROW_HEADER_STRIPE_HEIGHT). */
  firstRowHeight: number;
  hoveredComponentId: ArdoqId | null;
  focusedComponentId: ArdoqId | null;
  contextComponentId: ArdoqId | null;
  commands: ComponentMatrix2Commands;
};
const ComponentMatrixRowHeaderCell = ({
  headerCell,
  rowIndex,
  cellBottomGap,
  firstRowHeight,
  hoveredComponentId,
  focusedComponentId,
  contextComponentId,
  commands,
}: ComponentMatrixRowHeaderCellArgs) => {
  if (headerCell.group.isHidden) {
    return null;
  }
  const isPadding =
    headerCell.value === COMPONENT_HIERARCHY_PADDING ||
    headerCell.group.ownerType === ComponentMatrixGroupOwnerType.EMPTY;
  if (isPadding) {
    return null;
  }
  const isComponent =
    headerCell.group.ownerType ===
    ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT;

  const { fillColor, borderColor, isDark } = headerCell;
  const isLeafOrCollapsed =
    !headerCell.group.isExpanded || !hasChildren(headerCell.group);
  const showGroupOutline =
    isComponent && headerCell.value && !isLeafOrCollapsed;
  const { spanStyle, headerStyle } = getRowHeaderCardStyle(
    headerCell,
    fillColor,
    borderColor
  );

  const margins = getRowHeaderMargins(headerCell);
  const level = headerCell.indexAddress.length - 1;
  // Component cells at level>0 (leaf, collapsed, or expanded) fill the full track
  // because their row height equals MIN_HEADER_CELL_HEIGHT with no vertical margins.
  const isComponentAtChildLevel = isComponent && level > 0;
  // Last-sibling leaf/collapsed component at any level > 0: reserve space below the
  // card so it stops 8px before the innermost GroupOutline bottom border, then
  // each outer border adds another 8px step (the staircase effect).
  const staircaseGap =
    isComponent && isLeafOrCollapsed && level > 0 && isLastChild(headerCell)
      ? level * CARD_GROUP_BOTTOM_MARGIN
      : 0;
  const cardTopOffset = margins?.marginTop ?? 0;
  // Expanded-group stripe height. Two cases:
  //  - Level 0: fill the full first sub-row so the colored bar matches the
  //    body cell's height in that row.
  //  - Deeper levels: use the cell's wrap-aware stripe height (the wrapped
  //    label height precomputed in the view model via wrapString +
  //    canvasLabelMeasurer) so multi-line labels aren't clipped to the
  //    constant ROW_HEADER_STRIPE_HEIGHT.
  // Floor at ROW_HEADER_STRIPE_HEIGHT so single-line labels still show a
  // usable label area.
  const expandedStripeHeight =
    level === 0
      ? Math.max(ROW_HEADER_STRIPE_HEIGHT, firstRowHeight)
      : Math.max(
          ROW_HEADER_STRIPE_HEIGHT,
          headerCell.stripeHeight ?? ROW_HEADER_STRIPE_HEIGHT
        );
  // Center the label only when it is single-line AND its row is at the minimum
  // height. Top-align otherwise — union of the line-count rule (multi-line labels
  // anchor their icon/badge to the first line, ARD-31373) and the min-height rule
  // (a row stretched past its minimum keeps the label visible at the top,
  // ARD-32762). An expanded stripe sized to a tall first body row is covered by
  // the min-height branch.
  const isMinHeight =
    (headerCell.labelLineCount ?? 1) > 1 ? false : headerCell.isMinHeight;
  // Expanded cards use `height` rather than `minHeight` because the
  // GroupOutline overlay's `top` is set from the same `expandedStripeHeight`
  // value (so the outline starts exactly where the stripe ends). If the
  // card grew past `expandedStripeHeight` via `minHeight` while the
  // outline's `top` stayed at the smaller value, the outline's left/right
  // borders would visibly intrude into the stripe area. Better to clip the
  // rare measurement-drift case than to risk a layout glitch in the common one.
  const cardStyle: CSSProperties = isLeafOrCollapsed
    ? {
        ...headerStyle,
        ...(margins === null || isComponentAtChildLevel
          ? {
              height: `calc(100% - ${cardTopOffset + cellBottomGap + staircaseGap}px)`,
            }
          : {}),
      }
    : {
        ...headerStyle,
        height: expandedStripeHeight,
      };
  const headerCellStyle: CSSProperties = {
    gridRow: `${rowIndex + 1} / ${rowIndex + 1 + headerCell.span}`,
    gridColumn: '1 / 2',
    ...(showGroupOutline ? { position: 'relative' } : {}),
    // Deeper-level HeaderCells span the same grid rows and stack on top. Without
    // this, their transparent 100%-height bodies intercept clicks before they
    // reach the parent card. Events on the card still bubble up to HeaderCell.
    pointerEvents: 'none',
    ...margins,
  };

  // HeaderCell has height: 100% (= grid track) plus marginTop, so its bottom edge
  // overflows the grid area by CARD_GROUP_TOP_MARGIN. Compensate so the GroupOutline
  // bottom lands at the intended position rather than being pushed down by the margin.
  // Level-0 cells have no marginTop so no compensation is needed there.
  const groupOutlineBottom =
    cellBottomGap +
    level * CARD_GROUP_BOTTOM_MARGIN +
    (level > 0 ? CARD_GROUP_TOP_MARGIN : 0);

  if (isComponent && headerCell.value) {
    const componentId = headerCell.value;
    return (
      <HeaderCell
        style={headerCellStyle}
        onMouseEnter={() => commands.onItemMouseEnter(componentId)}
        onMouseLeave={commands.onItemMouseLeave}
        onClick={e => {
          e.stopPropagation();
          commands.onItemSelect(componentId);
          commands.onComponentClick(componentId);
        }}
      >
        {showGroupOutline && (
          <GroupOutline
            style={{
              ...spanStyle,
              top: expandedStripeHeight,
              bottom: groupOutlineBottom,
            }}
          />
        )}
        <RowHeaderComponent
          representationData={
            headerCell.representationData ?? DEFAULT_REPRESENTATION_DATA
          }
          isExpanded={headerCell.group.isExpanded}
          showExpander={hasChildren(headerCell.group)}
          expanderClicked={() =>
            commands.onGroupExpanderClick({
              dimension: ComponentMatrixDimension.ROW,
              address: headerCell.group.address,
            })
          }
          labelDoubleClicked={() =>
            commands.onComponentDoubleClick(componentId)
          }
          headerCell={headerCell}
          headerStyle={cardStyle}
          isDark={isDark}
          isHighlighted={
            hoveredComponentId === headerCell.value ||
            focusedComponentId === headerCell.value
          }
          isContext={contextComponentId === headerCell.value}
          wrapLabel
          isMinHeight={isMinHeight}
        />
      </HeaderCell>
    );
  }
  // For list field headers, ComponentMatrixListValueHeaderCellContainer has
  // height:100% which fills the full grid track (including any encoded gap).
  // paddingBottom shortens the content box so the child stops before the gap.
  const listFieldCellStyle: CSSProperties =
    cellBottomGap > 0
      ? { ...headerCellStyle, paddingBottom: cellBottomGap }
      : headerCellStyle;
  const listValueIsMinHeight =
    (headerCell.labelLineCount ?? 1) > 1 ? false : headerCell.isMinHeight;
  return (
    <HeaderCell style={listFieldCellStyle}>
      <ComponentMatrixListValueHeaderCellContainer
        $isMinHeight={listValueIsMinHeight}
      >
        <ListValueLabel {...truncationPopoverAttrs}>
          {headerCell.value || NONE_HEADER_LABEL}
        </ListValueLabel>
      </ComponentMatrixListValueHeaderCellContainer>
    </HeaderCell>
  );
};
export { ComponentMatrixRowHeaderCell };
